import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Loader2, AlertCircle, MapPin, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const LEAD_SOURCES = ['Manual', 'Website', 'Referral', 'Meta Campaign', 'Cold Call', 'Other'];

const LEAD_STATUSES = [
  'New Lead',
  'Uncontacted',
  'Qualified Lead',
  'Virtual Meet',
  'Awaited Visit',
  'Awaited LOI',
  'Closed LOI',
  'Outlet Opened',
  'Nurture',
  'Dead',
];

const PIN_REGEX = /^[1-9]\d{5}$/;

const LeadDialog = ({ open, onClose, onSuccess, executives = [], lead = null }) => {
  const { api, user, isManager } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    state: '',
    district: '',
    city: '',
    pinCode: '',
    status: '',
    leadSource: 'Manual',
    remarks: '',
    assignedExecutiveId: '',
    assignedExecutiveName: '',
  });

  // PIN-code intelligence state (Phase 6)
  // 'idle' | 'checking' | 'valid' | 'invalid_format' | 'not_found' | 'error'
  const [pinStatus, setPinStatus] = useState('idle');
  const [pinMessage, setPinMessage] = useState('');
  const pinDebounceRef = useRef(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      const initial = lead
        ? {
            name: lead.name || '',
            mobile: lead.mobile || '',
            email: lead.email || '',
            state: lead.state || '',
            district: lead.district || '',
            city: lead.city || '',
            pinCode: lead.pinCode || '',
            status: lead.status || '',
            leadSource: lead.leadSource || 'Manual',
            remarks: '',
            assignedExecutiveId: lead.assignedExecutiveId || '',
            assignedExecutiveName: lead.assignedExecutiveName || '',
          }
        : {
            name: '', mobile: '', email: '', state: '', district: '', city: '',
            pinCode: '', status: '', leadSource: 'Manual', remarks: '',
            assignedExecutiveId: '', assignedExecutiveName: '',
          };
      setFormData(initial);
      // Pre-validate existing PIN on open
      if (initial.pinCode && PIN_REGEX.test(initial.pinCode)) {
        setPinStatus('valid');
        setPinMessage('Locked from saved data.');
      } else {
        setPinStatus('idle');
        setPinMessage('');
      }
    }
  }, [open, lead]);

  // Debounced PIN lookup
  const lookupPin = async (pin) => {
    if (!PIN_REGEX.test(pin)) {
      setPinStatus('invalid_format');
      setPinMessage('PIN must be 6 digits and start with 1–9.');
      // Clear derived fields so user can't accidentally submit stale data
      setFormData((f) => ({ ...f, state: '', district: '', city: '' }));
      return;
    }
    setPinStatus('checking');
    setPinMessage('Looking up location...');
    try {
      const { data } = await api.get(`/pincode/${pin}`);
      setPinStatus('valid');
      setPinMessage(`✓ ${data.city}, ${data.district}, ${data.state}`);
      setFormData((f) => ({
        ...f,
        pinCode: data.pinCode,
        state: data.state || '',
        district: data.district || '',
        city: data.city || '',
      }));
    } catch (err) {
      if (err?.response?.status === 404) {
        setPinStatus('not_found');
        setPinMessage('PIN not found in India Post master. Please re-check.');
      } else if (err?.response?.status === 422) {
        setPinStatus('invalid_format');
        setPinMessage('PIN must be 6 digits and start with 1–9.');
      } else {
        setPinStatus('error');
        setPinMessage('Lookup failed. Check connection.');
      }
      setFormData((f) => ({ ...f, state: '', district: '', city: '' }));
    }
  };

  const handlePinChange = (raw) => {
    const cleaned = raw.replace(/\D/g, '').slice(0, 6);
    setFormData((f) => ({ ...f, pinCode: cleaned }));
    if (pinDebounceRef.current) clearTimeout(pinDebounceRef.current);
    if (!cleaned) {
      setPinStatus('idle');
      setPinMessage('');
      return;
    }
    if (cleaned.length < 6) {
      setPinStatus('idle');
      setPinMessage('Enter 6 digits to auto-fill State/District/City.');
      return;
    }
    pinDebounceRef.current = setTimeout(() => lookupPin(cleaned), 400);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.mobile) {
      toast.error('Name and mobile are required');
      return;
    }

    // Hard block: invalid PIN format / not in master
    if (formData.pinCode && (pinStatus === 'invalid_format' || pinStatus === 'not_found')) {
      toast.error('Please fix the PIN code or clear it before saving.');
      return;
    }

    if (!lead && !formData.status) {
      toast.error('Lead Status is mandatory. Please select a status.');
      return;
    }

    setSaving(true);
    try {
      if (lead) {
        const updateData = { ...formData };
        delete updateData.status;
        await api.put(`/leads/${lead.id}`, updateData);
        toast.success('Lead updated — State/District/City auto-synced from PIN');
      } else {
        await api.post('/leads', formData);
        toast.success('Lead created — Location auto-derived from PIN code');
      }
      onSuccess();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save lead'));
    } finally {
      setSaving(false);
    }
  };

  const handleExecutiveChange = (executiveId) => {
    const actualId = executiveId === 'unassigned' ? '' : executiveId;
    const exec = executives.find((e) => e.id === actualId);
    setFormData({
      ...formData,
      assignedExecutiveId: actualId,
      assignedExecutiveName: exec?.name || '',
    });
  };

  const isFormValid =
    formData.name &&
    formData.mobile &&
    (lead || formData.status) &&
    !(formData.pinCode && (pinStatus === 'invalid_format' || pinStatus === 'not_found'));

  // Lock state/district/city when PIN has been successfully resolved
  const locationLocked = pinStatus === 'valid';

  const pinHintIcon = () => {
    if (pinStatus === 'checking') return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    if (pinStatus === 'valid') return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
    if (pinStatus === 'invalid_format' || pinStatus === 'not_found' || pinStatus === 'error')
      return <XCircle className="h-4 w-4 text-destructive" />;
    return <MapPin className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{lead ? 'Edit Lead' : 'Add New Lead'}</DialogTitle>
          <DialogDescription>
            {lead ? 'Update lead information' : 'Enter the details for the new lead. PIN code auto-fills State, District & City.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="lead-name-input"
                />
              </div>
              <div>
                <Label htmlFor="mobile">Mobile *</Label>
                <Input
                  id="mobile"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  required
                  data-testid="lead-mobile-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="lead-email-input"
              />
            </div>

            {/* PHASE 6 — PIN-first location entry */}
            <div>
              <Label htmlFor="pinCode" className="flex items-center gap-2">
                Pin Code
                {pinHintIcon()}
              </Label>
              <Input
                id="pinCode"
                inputMode="numeric"
                maxLength={6}
                value={formData.pinCode}
                onChange={(e) => handlePinChange(e.target.value)}
                onBlur={() => formData.pinCode && lookupPin(formData.pinCode)}
                placeholder="6-digit India PIN (e.g. 400001)"
                data-testid="lead-pincode-input"
                className={
                  pinStatus === 'invalid_format' || pinStatus === 'not_found'
                    ? 'border-destructive'
                    : pinStatus === 'valid'
                    ? 'border-emerald-500'
                    : ''
                }
              />
              {pinMessage && (
                <p
                  className={`text-xs mt-1 ${
                    pinStatus === 'valid'
                      ? 'text-emerald-600'
                      : pinStatus === 'invalid_format' || pinStatus === 'not_found' || pinStatus === 'error'
                      ? 'text-destructive'
                      : 'text-muted-foreground'
                  }`}
                  data-testid="lead-pin-feedback"
                >
                  {pinMessage}
                </p>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="state">State {locationLocked && <span className="text-xs text-muted-foreground">(auto)</span>}</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  disabled={locationLocked}
                  data-testid="lead-state-input"
                  className={locationLocked ? 'bg-muted cursor-not-allowed' : ''}
                />
              </div>
              <div>
                <Label htmlFor="district">District {locationLocked && <span className="text-xs text-muted-foreground">(auto)</span>}</Label>
                <Input
                  id="district"
                  value={formData.district}
                  onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                  disabled={locationLocked}
                  data-testid="lead-district-input"
                  className={locationLocked ? 'bg-muted cursor-not-allowed' : ''}
                />
              </div>
              <div>
                <Label htmlFor="city">City {locationLocked && <span className="text-xs text-muted-foreground">(auto)</span>}</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  disabled={locationLocked}
                  data-testid="lead-city-input"
                  className={locationLocked ? 'bg-muted cursor-not-allowed' : ''}
                />
              </div>
            </div>

            {!lead && (
              <div>
                <Label htmlFor="status" className="flex items-center gap-1">
                  Lead Status *
                  <span className="text-destructive">
                    <AlertCircle className="h-3 w-3 inline" />
                  </span>
                </Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger
                    data-testid="lead-status-select"
                    className={!formData.status ? 'border-destructive' : ''}
                  >
                    <SelectValue placeholder="Select Lead Status (Required)" />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!formData.status && (
                  <p className="text-xs text-destructive mt-1">
                    Status selection is mandatory before creating a lead
                  </p>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="source">Lead Source</Label>
              <Select
                value={formData.leadSource}
                onValueChange={(v) => setFormData({ ...formData, leadSource: v })}
              >
                <SelectTrigger data-testid="lead-source-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_SOURCES.map((source) => (
                    <SelectItem key={source} value={source}>{source}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isManager && executives.length > 0 && (
              <div>
                <Label htmlFor="assignee">Assign To</Label>
                <Select
                  value={formData.assignedExecutiveId || 'unassigned'}
                  onValueChange={handleExecutiveChange}
                >
                  <SelectTrigger data-testid="lead-assignee-select">
                    <SelectValue placeholder="Select Executive" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {executives.map((exec) => (
                      <SelectItem key={exec.id} value={exec.id}>{exec.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label htmlFor="remarks">Remarks</Label>
              <Textarea
                id="remarks"
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                placeholder="Add any notes about this lead..."
                data-testid="lead-remarks-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !isFormValid}
              data-testid="save-lead-btn"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {lead ? 'Update' : 'Create'} Lead
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LeadDialog;
